import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useProjectStore from '../../store/projectStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const initialForm = {
  project_id: '',
  working_volume_liters: '',
  freeboard_percent: '15',
  hd_ratio: '1.5',
  design_pressure_bar: '',
  design_temperature_c: '150',
  material: 'SA-240 316L',
  allowable_stress_mpa: '',
  joint_efficiency: '0.85',
  corrosion_allowance_mm: '1.5',
  full_vacuum_rating: false,
  external_design_pressure_bar: '1.0',
  elastic_modulus_mpa: '',
  poisson_ratio: '0.3',
};

const TankSizingCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);
  const [showDetails, setShowDetails] = useState(true);
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { result, isCalculating, isSubmitting, error, calculate, saveCalculation, clearResult } = useDesignStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const buildInputs = () => {
    if (!form.working_volume_liters || !form.design_pressure_bar) {
      showToast('Working volume and design pressure are required', 'error');
      return null;
    }
    return {
      working_volume_liters: Number(form.working_volume_liters),
      freeboard_percent: Number(form.freeboard_percent || 15),
      hd_ratio: Number(form.hd_ratio || 1.5),
      design_pressure_bar: Number(form.design_pressure_bar),
      design_temperature_c: Number(form.design_temperature_c || 40),
      material: form.material,
      allowable_stress_mpa: form.allowable_stress_mpa === '' ? null : Number(form.allowable_stress_mpa),
      joint_efficiency: Number(form.joint_efficiency),
      corrosion_allowance_mm: Number(form.corrosion_allowance_mm),
      full_vacuum_rating: form.full_vacuum_rating,
      external_design_pressure_bar: Number(form.external_design_pressure_bar || 1.0),
      elastic_modulus_mpa: form.elastic_modulus_mpa === '' ? null : Number(form.elastic_modulus_mpa),
      poisson_ratio: Number(form.poisson_ratio || 0.3),
    };
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const inputs = buildInputs();
    if (!inputs) return;
    await calculate('tank_sizing', inputs);
  };

  const handleSave = async () => {
    if (!result) return;
    const payload = {
      calculator_type: 'tank_sizing',
      project_id: form.project_id ? Number(form.project_id) : null,
      created_by_id: user?.id || null,
      client_data: result.inputs,
      calculation_outputs: result.outputs,
    };
    const saveResult = await saveCalculation(payload);
    if (saveResult.success) {
      showToast('Calculation saved as draft', 'success');
      navigate('/design/calculations');
    } else {
      showToast(saveResult.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Tank Sizing (ASME Section VIII)</h1>
        <p className="text-xs text-gray-500 mb-6">
          Diameter/height derived from working volume + freeboard + H/D ratio. Cylindrical shell
          (UG-27) + standard torispherical ASME F&amp;D head (UG-32(e)) minimum thickness sizing
          only — not a complete certified vessel design. See "Assumptions" below the result.
          Requires review by a qualified pressure vessel engineer before fabrication.
        </p>

        <div className="glass-card p-6">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={form.project_id}
                onChange={handleChange('project_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Not linked to a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Working Volume (L) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.working_volume_liters}
                  onChange={handleChange('working_volume_liters')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Freeboard (%)</label>
                <input
                  type="number"
                  step="any"
                  value={form.freeboard_percent}
                  onChange={handleChange('freeboard_percent')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Standard: 15"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">H/D Ratio</label>
                <input
                  type="number"
                  step="any"
                  value={form.hd_ratio}
                  onChange={handleChange('hd_ratio')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Typical: 1.0–1.5"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Design Pressure (bar) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.design_pressure_bar}
                  onChange={handleChange('design_pressure_bar')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Design Temperature (°C)</label>
                <input
                  type="number"
                  step="any"
                  value={form.design_temperature_c}
                  onChange={handleChange('design_temperature_c')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                <input
                  type="text"
                  value={form.material}
                  onChange={handleChange('material')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allowable Stress, S (MPa)</label>
                <input
                  type="number"
                  step="any"
                  value={form.allowable_stress_mpa}
                  onChange={handleChange('allowable_stress_mpa')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Blank = typical default (confirm vs ASME II-D)"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Joint Efficiency, E</label>
                <input
                  type="number"
                  step="any"
                  max="1"
                  value={form.joint_efficiency}
                  onChange={handleChange('joint_efficiency')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Corrosion Allowance (mm)</label>
                <input
                  type="number"
                  step="any"
                  value={form.corrosion_allowance_mm}
                  onChange={handleChange('corrosion_allowance_mm')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <input
                  type="checkbox"
                  checked={form.full_vacuum_rating}
                  onChange={(e) => setForm((f) => ({ ...f, full_vacuum_rating: e.target.checked }))}
                />
                Also check full vacuum / external pressure rating
              </label>
              <p className="text-xs text-gray-400 mb-3">
                Screening approximation only (not the ASME UG-28 chart method) — see warnings on the result.
              </p>

              {form.full_vacuum_rating && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">External Pressure Differential (bar)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.external_design_pressure_bar}
                      onChange={handleChange('external_design_pressure_bar')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="Full vacuum = 1.0 (atmospheric outside vs. ~0 abs. inside)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Elastic Modulus, E (MPa)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.elastic_modulus_mpa}
                      onChange={handleChange('elastic_modulus_mpa')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="Blank = SS default (193000)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Poisson's Ratio</label>
                    <input
                      type="number"
                      step="any"
                      value={form.poisson_ratio}
                      onChange={handleChange('poisson_ratio')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="submit"
                disabled={isCalculating}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
          </form>
        </div>

        {result && (
          <div className="glass-card p-6 mt-4">
            <h2 className="font-semibold text-gray-900 mb-4">Result</h2>

            {result.outputs.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
                {result.outputs.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Geometry</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
              <Stat label="Inside Diameter" value={`${result.outputs.inside_diameter_mm} mm`} highlight />
              <Stat label="Straight Shell Height" value={`${result.outputs.straight_shell_height_mm} mm`} />
              <Stat label="Overall Height" value={`${result.outputs.overall_height_mm} mm`} highlight />
              <Stat label="Shell (Cylinder) Volume" value={`${result.outputs.cylinder_volume_liters} L`} />
              <Stat label="Dish Volume (each)" value={`${result.outputs.head_volume_liters} L`} />
              <Stat label="Total Volume (shell + bottom dish)" value={`${result.outputs.actual_total_volume_liters} L`} highlight />
              <Stat label="Working Volume" value={`${result.outputs.actual_working_volume_liters} L`} />
              <Stat label="Physical Volume (incl. top dish)" value={`${result.outputs.actual_physical_volume_liters} L`} />
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Thickness</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <Stat label="Allowable Stress (S)" value={`${result.outputs.allowable_stress_mpa} MPa${result.outputs.s_value_is_default ? ' (default)' : ''}`} />
              <Stat label="Shell Governing Case" value={result.outputs.shell_governing_case} />
              <Stat label="Shell Thickness (required)" value={`${result.outputs.shell_thickness_required_mm} mm`} />
              <Stat label="Shell Recommended Plate" value={`${result.outputs.shell_recommended_plate_mm} mm`} highlight />
              <Stat label="Head Thickness (required)" value={`${result.outputs.head_thickness_required_mm} mm`} />
              <Stat label="Head Recommended Plate" value={`${result.outputs.head_recommended_plate_mm} mm`} highlight />
            </div>

            {(result.outputs.calculation_steps || result.outputs.assumptions) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="text-xs font-semibold text-komal-blue hover:underline mb-3"
                >
                  {showDetails ? '− Hide calculation & assumptions' : '+ Show calculation & assumptions'}
                </button>

                {showDetails && (
                  <div className="space-y-4">
                    {result.outputs.calculation_steps?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Calculation Steps
                        </h3>
                        <div className="bg-gray-50 rounded border border-gray-100">
                          <ScrollX>
                          <table className="w-full text-xs">
                            <tbody>
                              {result.outputs.calculation_steps.map((step, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-0">
                                  <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{step.step}</td>
                                  <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                                  <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </ScrollX>
                        </div>
                      </div>
                    )}

                    {result.outputs.assumptions?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Assumptions
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {result.outputs.assumptions.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600">{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={() => clearResult()}
                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${highlight ? 'text-komal-blue text-lg' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default TankSizingCalculatorPage;
